<script setup lang="ts">
import type { ContentFile } from '~/models/Content/ContentFile'
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faDownload } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { Section } from '~/models/Content/Section'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useMedia } from '~/composables/useMedia'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import Image from '~/components/media/Image.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const { buildResourceUrl } = useUrlBuilder()
const { checkItemLicense } = useLicenseControlStore()
const { findContents } = useContentApi()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { isImage } = useContentHelper()

const { data: location, isError, isLoading } = useQuery({
  enabled: computed(() => props.locationId > 0),
  queryKey: computed(() => ['file-page', props.locationId]),
  queryFn: () => findContents<ContentFile>({
    subtreeCriterion: [Subtree.Content, Subtree.Media],
    contentTypeCriterion: [ContentType.File],
    locationIdCriterion: [Number(props.locationId)],
  }).then(([content]) => content),
  staleTime: Infinity,
  retry: 1,
})

const imageId = computed(() => Number(location.value?.image?.destinationContentId ?? 0))
const mediaContentId = computed(() => [imageId.value])
const { data: image, isFetching } = firstOf(useMedia(mediaContentId))

const downloadUrl = computed(() => {
  if (!isAuthenticated.value || !location.value)
    return ''
  const copy = { ...location.value }
  copy.sectionId = Section.Standard
  return buildResourceUrl(copy)
})

watch(location, () => {
  if (!location.value)
    return
  setTitle(location.value.title)
  checkItemLicense(location.value)
}, { immediate: true })
</script>

<template>
  <TopBar
    :item="location"
    :fullscreen="false"
  />
  <NotFound v-if="isError" />
  <template v-else>
    <div
      v-if="location || isLoading"
      class="grid h-full text-lg md:grid-cols-[4fr_6fr] md:grid-rows-[auto]"
      :style="{ '--palette-color': image && isImage(image) ? image?.palette : undefined }"
    >
      <div class="flex place-content-center items-start bg-(--palette-color,black) px-5 py-12 md:px-14">
        <FullscreenContent>
          <template #default="{ isFullscreen }">
            <Image
              v-if="image"
              :content="image"
              class="col-start-1 row-start-1"
              :class="{
                'max-h-full place-self-center bg-(--palette-color,black) object-contain p-4': isFullscreen,
                'mt-4 !w-[300px] md:!w-[500px]': !isFullscreen,
              }"
            />

            <CopyrightButton
              v-if="image && 'copyright' in image && image.copyright && !isFetching"
              class="col-start-1 row-start-1 place-self-end"
              :copyright="image.copyright"
            />

            <div
              v-if="image && 'caption' in image && image.caption"
              class="row-start-2 flex flex-col place-self-stretch bg-white p-4 text-black"
            >
              <RichTextRenderer :text="image.caption" />
            </div>
          </template>
        </FullscreenContent>
      </div>

      <div
        v-if="location"
        class="box-content flex max-w-prose flex-col gap-6 px-5 py-12 md:px-16"
      >
        <h1
          class="text-5xl font-bold"
          style="word-break: break-word;"
        >
          {{ location.title }}
        </h1>

        <div v-if="location.intro || location.description" class="au-subtitle au-prose contents">
          <RichTextRenderer v-if="location.intro" :text="location.intro" />
          <RichTextRenderer v-if="location.description" :text="location.description" />
        </div>

        <Button
          :href="downloadUrl"
          class="self-start"
          size="xl"
          :disabled="!downloadUrl"
        >
          <font-awesome-icon :icon="faDownload" />
          {{ t('download') }}
        </Button>
      </div>

      <div
        v-else-if="isLoading"
        class="box-content flex max-w-prose flex-col gap-6 px-5 py-12 md:px-16"
      >
        <AuSkeleton />

        <hr class="border-seagreen-10 -mt-3">

        <AuSkeletonWrapper
          :text="t('bookreader.loading')"
          class="contents"
        >
          <AuSkeleton height="3rem" />

          <div class="flex flex-wrap gap-4">
            <AuSkeleton width="33%" />
            <AuSkeleton width="33%" />
          </div>

          <div class="flex flex-col gap-3">
            <AuSkeleton width="50ch" />
            <AuSkeleton width="40ch" />
            <AuSkeleton width="45ch" />
            <AuSkeleton width="43ch" />
            <AuSkeleton width="52ch" />
            <AuSkeleton width="35ch" />
          </div>
        </AuSkeletonWrapper>
      </div>
    </div>
  </template>
</template>
